'use client';

import React, { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  Edit2, 
  Trash2, 
  Clock, 
  MapPin, 
  AlertCircle,
  FileText,
  ChevronRight,
  Share2,
  Download,
  Calendar,
  User,
  Activity
} from 'lucide-react';
import { useHealthLog, useDeleteHealthLog } from '@/features/health/hooks/useHealthLogs';
import { useChild } from '@/features/children/hooks/useChildren';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { PageHeader } from '@/components/layout/PageHeader';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { HealthLogTypeIcon } from '@/features/health/components/HealthLogTypeIcon';
import { SeverityIndicator } from '@/features/health/components/SeverityIndicator';
import { AttachmentList } from '@/components/shared/AttachmentList';
import { useToast } from '@/core/hooks/useToast';
import { formatDateTime, formatDuration, formatRelativeTime } from '@/utils/dateUtils';
import { getLogTypeColor, formatHealthLogData } from '@/features/health/utils/healthUtils';
import type { HealthLog } from '@/features/health/types/healthTypes';

export function HealthLogDetailPage(): React.ReactElement {
  const navigate = useNavigate();
  const { familyId, logId } = useParams<{ familyId: string; logId: string }>();
  const { showToast } = useToast();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Fetch health log
  const { 
    data: log, 
    isLoading: logLoading,
    error: logError,
  } = useHealthLog(logId!);

  // Fetch child info
  const { 
    data: child, 
    isLoading: childLoading,
  } = useChild(log?.childId || '');

  const deleteHealthLog = useDeleteHealthLog();

  const isLoading = logLoading || childLoading;

  // Format log data based on type
  const formattedData = useMemo(() => {
    if (!log) return null;
    return formatHealthLogData(log);
  }, [log]);

  const handleEdit = () => {
    navigate(`/family/${familyId}/health/logs/${logId}/edit`);
  };

  const handleDelete = async () => {
    try {
      await deleteHealthLog.mutateAsync(logId!);
      showToast({
        type: 'success',
        title: 'Log deleted',
        message: 'Health log entry has been removed.',
      });
      navigate(`/family/${familyId}/health/logs${log?.childId ? `?childId=${log.childId}` : ''}`);
    } catch (error) {
      showToast({
        type: 'error',
        title: 'Delete failed',
        message: 'Failed to delete health log. Please try again.',
      });
    }
  };

  const handleExport = () => {
    // TODO: Implement single log export
    showToast({
      type: 'info',
      title: 'Export',
      message: 'Single log export coming soon.',
    });
  };

  const handleShare = () => {
    // TODO: Implement share functionality
    showToast({
      type: 'info',
      title: 'Share',
      message: 'Share functionality coming soon.',
    });
  };

  const handleBack = () => {
    navigate(`/family/${familyId}/health/logs${log?.childId ? `?childId=${log.childId}` : ''}`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (logError || !log) {
    return (
      <div className="container mx-auto px-4 py-8">
        <EmptyState
          icon={<Activity className="w-12 h-12 text-warm-gray" />}
          title="Log not found"
          description="This health log entry doesn't exist or has been removed."
          action={
            <Button onClick={() => navigate(`/family/${familyId}/health/logs`)}>
              Back to Logs
            </Button>
          }
        />
      </div>
    );
  }

  const typeColor = getLogTypeColor(log.logType?.slug || 'other');

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Back Button */}
      <button
        onClick={handleBack}
        className="flex items-center text-warm-gray hover:text-navy mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Logs
      </button>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-8">
        <div className="flex items-start gap-4">
          <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${typeColor.bg}`}>
            <HealthLogTypeIcon 
              type={log.logType?.slug || 'other'} 
              className={`w-7 h-7 ${typeColor.text}`} 
            />
          </div>
          <div>
            <h1 className="text-2xl font-display font-semibold text-navy">
              {log.logType?.name || 'Health Log'}
            </h1>
            <div className="flex items-center gap-3 mt-1 text-warm-gray">
              <span className="flex items-center">
                <Calendar className="w-4 h-4 mr-1" />
                {formatDateTime(log.occurredAt)}
              </span>
              {log.duration_minutes && (
                <span className="flex items-center">
                  <Clock className="w-4 h-4 mr-1" />
                  {formatDuration(log.duration_minutes)}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={handleShare}>
            <Share2 className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={handleExport}>
            <Download className="w-4 h-4" />
          </Button>
          <Button variant="secondary" onClick={handleEdit}>
            <Edit2 className="w-4 h-4 mr-2" />
            Edit
          </Button>
          <Button 
            variant="secondary" 
            onClick={() => setShowDeleteDialog(true)}
            className="text-red-500 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Severity */}
          {log.severity && (
            <Card className="p-6">
              <h2 className="text-sm font-medium text-warm-gray uppercase tracking-wide mb-3">
                Severity
              </h2>
              <SeverityIndicator severity={log.severity} showLabel size="lg" />
            </Card>
          )}

          {/* Log Details */}
          <Card className="p-6">
            <h2 className="text-sm font-medium text-warm-gray uppercase tracking-wide mb-4">
              Details
            </h2>
            
            {formattedData && formattedData.length > 0 ? (
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                {formattedData.map((item, index) => (
                  <div key={index} className={item.fullWidth ? 'sm:col-span-2' : ''}>
                    <dt className="text-sm text-warm-gray mb-1">{item.label}</dt>
                    <dd className="text-navy font-medium">{item.value}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="text-warm-gray">No additional details recorded.</p>
            )}
          </Card>

          {/* Notes */}
          {log.notes && (
            <Card className="p-6">
              <h2 className="text-sm font-medium text-warm-gray uppercase tracking-wide mb-3">
                Notes
              </h2>
              <p className="text-navy whitespace-pre-wrap">{log.notes}</p>
            </Card>
          )}

          {/* Attachments */}
          {log.attachments && log.attachments.length > 0 && (
            <Card className="p-6">
              <h2 className="text-sm font-medium text-warm-gray uppercase tracking-wide mb-4">
                Attachments
              </h2>
              <AttachmentList attachments={log.attachments} />
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Child Info */}
          {child && (
            <Card className="p-6">
              <h2 className="text-sm font-medium text-warm-gray uppercase tracking-wide mb-3">
                Child
              </h2>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-sage/20 flex items-center justify-center">
                  <User className="w-5 h-5 text-sage" />
                </div>
                <div>
                  <p className="font-medium text-navy">
                    {child.first_name} {child.last_name}
                  </p>
                  <p className="text-sm text-warm-gray">
                    {child.nickname && `"${child.nickname}"`}
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate(`/family/${familyId}/children/${child.id}`)}
                className="mt-3 text-sm text-sage hover:text-sage-dark flex items-center"
              >
                View Profile
                <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            </Card>
          )}

          {/* Location */}
          {log.location && (
            <Card className="p-6">
              <h2 className="text-sm font-medium text-warm-gray uppercase tracking-wide mb-3">
                Location
              </h2>
              <div className="flex items-start gap-2">
                <MapPin className="w-5 h-5 text-warm-gray mt-0.5" />
                <p className="text-navy">{log.location}</p>
              </div>
            </Card>
          )}

          {/* Meta Information */}
          <Card className="p-6">
            <h2 className="text-sm font-medium text-warm-gray uppercase tracking-wide mb-3">
              Record Info
            </h2>
            <dl className="space-y-3 text-sm">
              <div>
                <dt className="text-warm-gray">Logged by</dt>
                <dd className="text-navy font-medium">
                  {log.loggedBy?.first_name} {log.loggedBy?.last_name}
                </dd>
              </div>
              <div>
                <dt className="text-warm-gray">Created</dt>
                <dd className="text-navy">{formatRelativeTime(log.createdAt)}</dd>
              </div>
              {log.updatedAt && log.updatedAt !== log.createdAt && (
                <div>
                  <dt className="text-warm-gray">Last Updated</dt>
                  <dd className="text-navy">{formatRelativeTime(log.updatedAt)}</dd>
                </div>
              )}
            </dl>
          </Card>

          {/* Related Actions */}
          <Card className="p-6">
            <h2 className="text-sm font-medium text-warm-gray uppercase tracking-wide mb-3">
              Actions
            </h2>
            <div className="space-y-2">
              <button
                onClick={() => navigate(`/family/${familyId}/health/logs/new?childId=${log.childId}&type=${log.logType?.id}`)}
                className="w-full text-left px-3 py-2 rounded-lg text-sm text-navy hover:bg-cream transition-colors flex items-center"
              >
                <Activity className="w-4 h-4 mr-2 text-sage" />
                Log Similar Entry
              </button>
              <button
                onClick={() => navigate(`/family/${familyId}/health/reports?childId=${log.childId}&type=${log.logType?.slug}`)}
                className="w-full text-left px-3 py-2 rounded-lg text-sm text-navy hover:bg-cream transition-colors flex items-center"
              >
                <FileText className="w-4 h-4 mr-2 text-sage" />
                View {log.logType?.name} Reports
              </button>
            </div>
          </Card>
        </div>
      </div>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDelete}
        title="Delete Health Log"
        description="Are you sure you want to delete this health log entry? This action cannot be undone."
        confirmLabel="Delete"
        confirmVariant="danger"
        isLoading={deleteHealthLog.isPending}
      />
    </div>
  );
}

export default HealthLogDetailPage;


